import React, { useMemo } from 'react';
import theme from './OptionRoomLinkEditor.module.scss';
import { useRooms } from '@/services/rooms';
import { Card, Checkbox } from '@ygtechnologies/rubus-ui';
import { PricingFieldSelect } from '@/seller/components/project/ProjectSettings/PricingFieldSelect';
import { RoomDto } from '@/types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { RoomLinkUpdateParams } from '@/services/options/option-groups';
import { Trans } from '@lingui/react/macro';
import { checkSubsetSelection } from '@/utils/arrays';
import { classnames } from '@ygtechnologies/bygr-frontend-component-library';
import RoomLabel from '@/components/RoomLabel';

export interface OptionRoomLinkEditorProps {
    projectId: number;
    value: RoomLinkUpdateParams[];
    onChange(newValue: RoomLinkUpdateParams[]): void;
    fillContainer?: boolean;
    /** Rooms with a live order - locked so they cannot be unlinked. */
    lockedRoomIds?: number[];
}

export const OptionRoomLinkEditor: React.FC<OptionRoomLinkEditorProps> = ({
    projectId,
    value,
    onChange,
    fillContainer = true,
    lockedRoomIds,
}) => {
    const { data: rooms } = useRooms(projectId);

    const lockedSet = useMemo(() => new Set(lockedRoomIds ?? []), [lockedRoomIds]);

    const data = useMemo(() => {
        if (!rooms) return null;
        const map = new Map<number, RoomLinkUpdateParams>();
        value.forEach((item) => map.set(item.roomId, item));

        return rooms.map((x) => {
            const selection = map.get(x.id);
            return {
                room: x,
                pricingFieldId: selection?.pricingFieldId ?? null,
                selected: !!selection,
                locked: lockedSet.has(x.id),
            };
        });
    }, [value, rooms, lockedSet]);

    const handlePricingVariableChange = (room: RoomDto, pricingFieldId: number) => {
        onChange(
            value.map((x) => {
                if (x.roomId === room.id) return { ...x, pricingFieldId: pricingFieldId };
                return x;
            })
        );
    };

    // When every linked room shares the same pricing variable, reflect it in the "apply to all" control;
    // otherwise leave it unset. Setting it applies that variable to all linked rooms at once, restoring
    // the single-field ergonomics of the pre-v2 group-level pricing on top of the per-room model.
    const commonPricingFieldId = useMemo(() => {
        if (value.length === 0) return null;
        const first = value[0].pricingFieldId;
        return value.every((x) => x.pricingFieldId === first) ? first : null;
    }, [value]);

    const handleApplyPricingToAll = (pricingFieldId: number) => {
        onChange(value.map((x) => ({ ...x, pricingFieldId })));
    };

    const handleSelectedChange = (room: RoomDto, selected: boolean) => {
        // A room with a live order cannot be unlinked - ignore the uncheck.
        if (!selected && lockedSet.has(room.id)) return;
        if (!selected) return onChange(value.filter((x) => x.roomId !== room.id));
        return onChange([...value, { roomId: room.id, pricingFieldId: null }]);
    };

    const handleSelectAll = (flag: boolean) => {
        if (!rooms) return;

        if (flag) {
            // Preserve existing links (incl. their pricing variable) rather than resetting every room.
            onChange(
                rooms.map((room) => value.find((x) => x.roomId === room.id) ?? { roomId: room.id, pricingFieldId: null })
            );
        } else {
            // Deselecting all still keeps locked (ordered) rooms linked.
            onChange(value.filter((x) => lockedSet.has(x.roomId)));
        }
    };

    const checkStatus = checkSubsetSelection(
        value.map((x) => x.roomId),
        (rooms ?? []).map((x) => x.id)
    );

    return (
        <div className={classnames(theme.container, { [theme.fillContainer]: fillContainer })}>
            <div className={theme.header}>
                <Checkbox
                    checked={checkStatus === 'all'}
                    indeterminate={checkStatus === 'some'}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    inputSize="md">
                    <Trans>Select All</Trans>
                </Checkbox>
                {value.length > 1 && (
                    <div className={theme.applyAll}>
                        <span className={theme.applyAllLabel}>
                            <Trans>Set pricing variable for all rooms</Trans>
                        </span>
                        <PricingFieldSelect
                            projectId={projectId}
                            value={commonPricingFieldId}
                            onChange={(pricingFieldId) => handleApplyPricingToAll(pricingFieldId)}
                        />
                    </div>
                )}
            </div>
            <div className={classnames(theme.roomList, { [theme.fillContainer]: fillContainer })}>
                {data?.map((item) => (
                    <Card
                        key={item.room.id}
                        isChecked={item.selected}
                        onCheck={() => handleSelectedChange(item.room, !item.selected)}>
                        <div className={theme.room}>
                            <div className={theme.roomInfo}>
                                <span className={theme.label}>
                                    <Trans>Name</Trans>
                                </span>
                                <div className={theme.roomNameValue}>
                                    {item.room.icon && (
                                        <FontAwesomeIcon icon={['fal', item.room.icon]} className={theme.icon} fixedWidth />
                                    )}
                                    <RoomLabel name={item.room.name} labelNote={item.room.labelNote} />
                                    {item.locked && (
                                        <span className={theme.lockedHint}>
                                            <Trans>Ordered</Trans>
                                        </span>
                                    )}
                                </div>
                            </div>
                            <div className={theme.roomInfo}>
                                <span className={theme.label}>
                                    <Trans>Pricing variable</Trans>
                                </span>
                                <PricingFieldSelect
                                    disabled={!item.selected}
                                    projectId={projectId}
                                    value={item.pricingFieldId}
                                    onChange={(pricingFieldId) => handlePricingVariableChange(item.room, pricingFieldId)}
                                />
                            </div>
                        </div>
                    </Card>
                ))}
            </div>
        </div>
    );
};
